// src/components/BoardList.tsx

import { useEffect, useState, useRef } from "react";
import { Post } from "../types/board";
import { usePosts } from "../contexts/PostContext";
import { getCategoryLabel } from "../constants/categories";
import {
  Image as ImageIcon,
  Video as VideoIcon,
  Youtube as YoutubeIcon,
} from "lucide-react";
import { Notice } from "../contexts/NoticeContext";
import AdSlot from "./ads/AdSlot";

export interface BoardListProps {
  posts: Post[];
  allPosts?: Post[];
  page?: number;
  pageSize?: number;
  onRowClick: (post: Post) => void;
  pinnedNotices?: Notice[];
  onNoticeClick?: (notice: Notice) => void;
  showAds?: boolean;
}

/** 작성자 익명 처리 */
function getDisplayName(post: Post) {
  const boardType = (post as any).board_type;

  // 익명게시판 → 무조건 익명
  if (boardType === "익명게시판") return "익명";

  // 일반 게시판 → 닉네임
  return post.nickname || "익명";
}

export default function BoardList({
  posts,
  allPosts,
  page,
  pageSize,
  onRowClick,
  pinnedNotices = [],
  onNoticeClick,
  showAds = true,
}: BoardListProps) {
  const { likes } = usePosts();
  const [isMobile, setIsMobile] = useState(false);

  /** PC/모바일 반응형 체크 */
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /** 추천수 계산 */
  function countLikes(postId?: number) {
    return postId
      ? likes.filter(
          (l: any) =>
            l.target_type === "post" &&
            l.target_id === postId &&
            l.vote === 1
        ).length
      : 0;
  }

  const prevBestRef = useRef<Post[]>([]);
  const pool: Post[] =
    allPosts && allPosts.length > 0 ? allPosts : posts;

  /** 최근 14일 기준 베스트 */
  const now = new Date();
  const fourteenDaysAgo = new Date();
  fourteenDaysAgo.setDate(now.getDate() - 14);

  const candidates = pool.filter((post) => {
    const comments = (post as any).comments?.[0]?.count ?? 0;
    const likesCount = countLikes((post as any).id);
    const postDate = post.date ? new Date(post.date) : null;

    return (
      comments >= 2 &&
      likesCount >= 3 &&
      postDate &&
      postDate >= fourteenDaysAgo
    );
  });

  function calcScore(post: Post) {
    const comments = (post as any).comments?.[0]?.count ?? 0;
    const likesCount = countLikes((post as any).id);
    const views = (post as any).views ?? 0;
    return likesCount * 3 + comments * 4 + views;
  }

  let bestPosts: Post[] = [];
  if (pool.length > 0) {
    bestPosts = candidates
      .slice()
      .sort((a, b) => calcScore(b) - calcScore(a))
      .slice(0, 5);
    prevBestRef.current =
      bestPosts.length > 0 ? bestPosts : prevBestRef.current;
  } else {
    bestPosts = prevBestRef.current;
  }

  const bestIds = new Set(bestPosts.map((p: any) => p.id));
  const normalPosts = posts.filter((p: any) => !bestIds.has(p.id));

  const totalNormalCount =
    allPosts && allPosts.length > 0
      ? allPosts.filter((p: any) => !bestIds.has(p.id)).length
      : normalPosts.length;

  const prefix = page && pageSize ? (page - 1) * pageSize : 0;

  const bestBefore =
    page && pageSize && allPosts
      ? allPosts
          .slice(0, Math.min(prefix, allPosts.length))
          .filter((p: any) => bestIds.has(p.id)).length
      : 0;

  const globalNormalStart = prefix - bestBefore;

  function displayNoFor(idx: number, fallbackId?: number) {
    if (allPosts && page && pageSize) {
      const globalIdx = globalNormalStart + idx;
      const no = totalNormalCount - globalIdx;
      if (Number.isFinite(no) && no > 0) return no;
    }
    return typeof fallbackId === "number"
      ? fallbackId
      : idx + 1;
  }

  // ================================
  //            PC VERSION
  // ================================
  if (!isMobile) {
    return (
      <div>
        {/* 광고 */}
        {showAds && (
          <div className="flex justify-center mb-2">
            <div className="w-full rounded-md border border-gray-200 bg-white px-0 ">
              <AdSlot code="top-banner" aspect={7.5} />
            </div>
          </div>
        )}

        {/* ⭐ 공지사항 */}
        {pinnedNotices.length > 0 && (
          <div className="mb-4">
            {pinnedNotices.map((notice) => (
              <div
                key={notice.id}
                className="border-l-4 border-yellow-400 bg-yellow-50 rounded mb-2 px-3 py-2 cursor-pointer"
                onClick={() =>
                  onNoticeClick && onNoticeClick(notice)
                }
              >
                <div className="font-semibold text-sm">{notice.title}</div>

                <div className="text-xs text-gray-600">
                  {notice.created_at
                    ? new Date(notice.created_at).toLocaleDateString()
                    : ""}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ⭐ 베스트 */}
        {bestPosts.length > 0 && (
          <div className="mb-4">
            {bestPosts.map((post: any, idx: number) => {
              const label = getCategoryLabel(
                post.board_type,
                post.category
              );
              const ccount =
                post.comments?.[0]?.count ?? 0;
              const lcount = countLikes(post.id);

              return (
                <div
                  key={post.id}
                  className="border-l-4 border-blue-400 bg-white rounded mb-2 px-2 py-2 cursor-pointer"
                  onClick={() => onRowClick(post)}
                >
                  <div className="flex items-center mb-1">
                    <span className="inline-block bg-gray-200 text-gray-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mr-2">
                      {idx + 1}
                    </span>
                    <span className="font-semibold mr-2">{post.title}</span>

                    {ccount > 0 && (
                      <span className="text-[#e6458b] text-xs ml-1">
                        [{ccount}]
                      </span>
                    )}
                    {post.has_youtube && (
                      <YoutubeIcon
                        size={16}
                        className="inline ml-1 text-red-600"
                      />
                    )}

                    <span className="ml-3 text-xs text-gray-500">
                      {label && <span>{label} | </span>}
                      {getDisplayName(post)}
                      {post.date && (
                        <> | {new Date(post.date).toLocaleDateString()}</>
                      )}
                      <span className="ml-2">
                        추천 {lcount}
                      </span>
                      <span className="ml-2">
                        조회 {post.views ?? 0}
                      </span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 일반 게시글 테이블 */}
        <div className="overflow-x-auto">
          <table className="w-full border text-sm md:text-base table-auto">
            <thead>
              <tr className="bg-gray-100 border-b-2">
                <th className="w-12 px-2">No</th>
                <th className="w-20 px-2">카테고리</th>
                <th className="min-w-[150px] max-w-[300px] px-2">제목</th>
                <th className="w-28 px-2">작성자</th>
                <th className="w-24 px-2">날짜</th>
                <th className="w-20 px-2">추천</th>
                <th className="w-20 px-2">조회</th>
              </tr>
            </thead>

            <tbody>
              {normalPosts.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="py-12 text-center text-gray-400"
                  >
                    게시글이 없습니다.
                  </td>
                </tr>
              )}

              {normalPosts.map((post: any, idx: number) => {
                const label = getCategoryLabel(
                  post.board_type,
                  post.category
                );
                const ccount =
                  post.comments?.[0]?.count ?? 0;
                const lcount = countLikes(post.id);

                return (
                  <tr
                    key={post.id}
                    className="cursor-pointer hover:bg-gray-50 border-b"
                    onClick={() => onRowClick(post)}
                  >
                    <td className="text-center">
                      {displayNoFor(idx, post.id)}
                    </td>
                    <td className="text-center">
                      {label && (
                        <span className="inline-block bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs">
                          {label}
                        </span>
                      )}
                    </td>
                    <td className="px-2 break-words">
                      {post.title}
                      {ccount > 0 && (
                        <span className="text-[#e6458b] ml-1">
                          [{ccount}]
                        </span>
                      )}
                    </td>
                    <td className="text-center">
                      {getDisplayName(post)}
                    </td>
                    <td className="text-center">
                      {post.date
                        ? new Date(post.date).toLocaleDateString()
                        : ""}
                    </td>
                    <td className="text-center">{lcount}</td>
                    <td className="text-center">
                      {post.views ?? 0}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* 하단 광고 */}
        {showAds && (
          <div className="flex justify-center my-6">
            <div className="w-full max-w-[1140px] ">
              <div className="rounded-xl border border-gray-200 bg-white px-3 py-3">
                <AdSlot code="footer" aspect={9.5} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ================================
  //        MOBILE VERSION
  // ================================
  return (
    <div>
      {/* ⭐ 모바일 공지 */}
      {pinnedNotices.length > 0 && (
        <div className="mb-4">
          {pinnedNotices.map((notice) => (
            <div
              key={notice.id}
              className="border-l-4 border-yellow-400 bg-yellow-50 rounded mb-2 px-3 py-2 cursor-pointer"
              onClick={() =>
                onNoticeClick && onNoticeClick(notice)
              }
            >
              <div className="font-semibold text-sm">{notice.title}</div>

              <div className="text-xs text-gray-600">
                {notice.created_at
                  ? new Date(notice.created_at).toLocaleDateString()
                  : ""}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 일반글 카드 */}
      <div className="flex flex-col gap-2">
        {normalPosts.length === 0 && (
          <div className="py-10 text-gray-400 text-center">
            게시글이 없습니다.
          </div>
        )}

        {normalPosts.map((post: any, idx: number) => {
          const label = getCategoryLabel(
            post.board_type,
            post.category
          );
          const ccount =
            post.comments?.[0]?.count ?? 0;
          const lcount = countLikes(post.id);

          return (
            <div
              key={post.id}
              className="border bg-white rounded p-3 cursor-pointer"
              onClick={() => onRowClick(post)}
            >
              <div className="font-bold mb-1 flex flex-wrap gap-1">
                <span>{post.title}</span>
                {ccount > 0 && (
                  <span className="text-[#e6458b] text-xs">
                    [{ccount}]
                  </span>
                )}
              </div>

              <div className="flex justify-between text-xs text-gray-500">
                <span>
                  {label && <span>{label} | </span>}
                  {getDisplayName(post)}
                  {post.date && (
                    <> | {new Date(post.date).toLocaleDateString()}</>
                  )}
                </span>
                <span>
                  추천 {lcount} · 조회 {post.views ?? 0}
                </span>
              </div>

              <div className="mt-2 text-xs text-gray-400">
                No. {displayNoFor(idx, post.id)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
